import React, { useEffect, useState, useCallback } from 'react';

import { retrieveFormsForVisits } from '../../../../services/FormService';
import { hasRoles, mergeRoles, CREATE_STUDY_ROLES, EDIT_STUDY_ROLES } from '../../../../util/RoleUtil';

import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { VisitPackCreation } from './VisitPackCreation';
import { FormAssignments } from './form-assignments/FormAssignments';
import { CancelConfirmationModal } from '../../../common/cancel-confirmation-modal/CancelConfirmationModal';

import './StudyTrialVisitPacksTab.css';

const roles = mergeRoles(CREATE_STUDY_ROLES, EDIT_STUDY_ROLES);

export const StudyTrialVisitPacksTab = ({
  context,
  setContext,
  setSpinnerText,
  showToaster,
  editMode,
  navigateTo,
  visits,
  setVisits,
  study,
  handleUpdate,
  amountOfVisits,
  phase,
  setPhase,
  setNumberOfVisits,
  schedules,
  scheduleData,
  isNewSchedule,
  scheduleName,
  scheduleExternalReference,
  setCurrentScreen,
  setShowSitesTab,
  enrolmentVisitAllowed,
  scheduleHasParticipantsLinked = false,
}) => {
  const [forms, setForms] = useState([]);
  const [visit, setVisit] = useState({});
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const fieldsDisabled = !hasRoles(context, roles);
  const visitPackEditMode = editMode && !isNewSchedule;

  const filterFormsForSchedule = useCallback(
    (allForms = []) => {
      return allForms.filter((form) => {
        if (!form?.scheduleSpecific) {
          return true;
        }
        return form.studyScheduleExternalReference === scheduleExternalReference;
      });
    },
    [scheduleExternalReference]
  );

  const retrieveAvailableForms = useCallback(async () => {
    setSpinnerText('Retrieving Available Forms');
    const [data, error] = await retrieveFormsForVisits(context.username);

    if (data) {
      if (data.responseStatus === 'Success') {
        setForms(filterFormsForSchedule(data.availableForms || []));
      } else {
        showToaster(data);
      }
    } else {
      showToaster({ responseStatus: 'Error', responseMessage: error.message });
    }

    setSpinnerText(null);
  }, [context.username, setSpinnerText, showToaster, filterFormsForSchedule]);

  useEffect(() => {
    if (!editMode) {
      retrieveAvailableForms();
    } else {
      setForms(filterFormsForSchedule(study?.forms || []));
    }
  }, [editMode, retrieveAvailableForms, study?.forms, filterFormsForSchedule]);

  const closeCancelModal = () => setIsCancelModalOpen(false);

  const clearBreadcrumbs = () => {
    setContext((prevState) => ({
      ...prevState,
      breadcrumbs: {
        newStudyGeneralBreadcrumb: undefined,
        newStudyFormsBreadcrumb: undefined,
        newStudySitesBreadcrumb: undefined,
      },
    }));
  };

  const handleCancelClick = () => {
    clearBreadcrumbs();
    navigateTo('studyTrialAdmin');
  };

  const handleAddFormsClick = (visitsCreated) => {
    setContext((prevState) => ({
      ...prevState,
      viewMode: false,
      breadcrumbs: {
        ...prevState.breadcrumbs,
        newStudyFormsBreadcrumb: {
          ...prevState.breadcrumbs.newStudyFormsBreadcrumb,
          type: 'General',
          screenKey: 'newStudyFormsBreadcrumb',
          data: visitsCreated,
        },
      },
      editMode: true,
    }));

    setVisits(visitsCreated);
    setPhase('FORMS');
  };

  const handleSaveAndNextClick = () => {
    setSpinnerText('Creating Study');

    const createStudyStudyScheduleVOS = (schedules || [])
      .map((s, scheduleIndex) => {
        const sched = scheduleData?.[s.id];
        if (!sched) return null;

        const scheduleName = (s.name || sched.scheduleName || '').trim();
        const scheduleVisits = Array.isArray(sched.visits) ? sched.visits : [];

        return {
          name: scheduleName,
          createStudyVisitScheduleVOS: scheduleVisits.map((v) => ({
            day: Number(v.day) || 0,
            formExternalReferences: v.formExternalReferences || [],
            longVisit: !!v.longVisit,
            visitCode: v.visitCode,
            visitType: v.visitType,
            windowAfter: Number(v.windowAfter) || 0,
            windowBefore: Number(v.windowBefore) || 0,
            enrolmentVisit: !!v.enrolmentVisit,
          })),
        };
      })
      .filter(Boolean)
      .filter((s) => s.name && s.createStudyVisitScheduleVOS.length > 0);

    if (createStudyStudyScheduleVOS.length === 0) {
      showToaster({
        responseStatus: 'Warning',
        responseMessage: 'Please create at least one schedule with at least one visit before saving.',
      });
      setSpinnerText(null);
      return;
    }

    const hasAtLeastOneAssignedForm = createStudyStudyScheduleVOS.some((schedule) =>
      (schedule.createStudyVisitScheduleVOS || []).some(
        (visit) => Array.isArray(visit.formExternalReferences) && visit.formExternalReferences.length > 0
      )
    );

    if (!hasAtLeastOneAssignedForm) {
      showToaster({
        responseStatus: 'Warning',
        responseMessage: 'Please assign at least one form to a visit before continuing.',
      });
      setSpinnerText(null);
      return;
    }

    setCurrentScreen('studyTrialSites');
    setShowSitesTab(true);
  };

  const handleUpdateSaveChangesClick = () => {
    const studyRoles = [];
    (study?.siteStaff || []).forEach((staff) => {
      staff.studyRole.split(', ').forEach((role) => {
        studyRoles.push({
          roleName: role,
          personExternalReferences: [staff.personExternalReference],
        });
      });
    });

    const updateStudyStudyScheduleVOS = (schedules || [])
      .map((s, scheduleIndex) => {
        const sched = scheduleData?.[s.id];
        if (!sched) return null;

        const name = (s.name || sched.scheduleName || '').trim();
        const scheduleVisits = Array.isArray(sched.visits) ? sched.visits : [];

        const updateStudyVisitScheduleVOS = scheduleVisits.map((v) => {
          const formRefs =
            Array.isArray(v.formExternalReferences) && v.formExternalReferences.length > 0
              ? v.formExternalReferences
              : Array.isArray(v.forms)
              ? v.forms.map((f) => f.formExternalReference)
              : [];

          const nextVisitCode = v.visitCodeEdited ? v.visitCodeToUpdate : v.visitCode;

          return {
            day: Number(v.day) || 0,
            enrolmentVisit: !!v.enrolmentVisit,
            formExternalReferences: formRefs,
            longVisit: !!v.longVisit,
            visitCode: nextVisitCode,
            ...(v.visitCodeEdited && { visitCodeToUpdate: v.visitCode }),
            visitType: v.visitType,
            windowAfter: Number(v.windowAfter) || 0,
            windowBefore: Number(v.windowBefore) || 0,
          };
        });

        const scheduleVO = {
          name,
          updateStudyVisitScheduleVOS,
        };

        if (sched.studyScheduleExternalReference) {
          scheduleVO.studyScheduleExternalReference = sched.studyScheduleExternalReference;
        }

        return scheduleVO;
      })
      .filter(Boolean)
      .filter((s) => s.name);

    const studyVO = {
      accrualPeriodFrom: study.accrualPeriodFrom,
      accrualPeriodTo: study.accrualPeriodTo,
      alertThresholdForDataCapture: Number(study.alertThresholdForDataCapture) || 0,
      alertThresholdForSDVCapture: Number(study.alertThresholdForSDVCapture) || 0,
      coEnrolmentAllowed: !!study.coEnrolmentAllowed,
      description: study.description || '',
      endDate: study.endDate,
      facilityExternalReference: study.facilityExternalReference,
      facilityExternalReferenceToUpdate: study.facilityExternalReference,
      firstEnrolmentDate: study.firstEnrolmentDate || '',
      protocolID: study.protocolID,
      roles: studyRoles,
      startDate: study.startDate,
      studyDurationPerParticipant: Number(study.studyDurationPerParticipant) || 0,
      studyDurationPerParticipantMetric: study.studyDurationPerParticipantMetric,
      studyExternalReference: study.studyExternalReference,
      studyName: study.studyName,
      studyType: study.studyType,
      updateStudyStudyScheduleVOS,
    };

    handleUpdate(studyVO);
  };

  return (
    <div className="tab-container">
      <h3 className="schedule-header">{scheduleName}</h3>
      {phase === 'VISITS' && (
        <div className="visit-pack-creation-container">
          <VisitPackCreation
            numberOfVisits={Number(amountOfVisits) || 0}
            setNumberOfVisits={setNumberOfVisits}
            handleAddFormsClick={handleAddFormsClick}
            editMode={visitPackEditMode}
            initialVisits={visits}
            visit={null}
            forms={forms}
            setVisitCodeEdited={() => {}}
            pageSize={5}
            initial
            study={study}
            enrolmentVisitAllowed={enrolmentVisitAllowed}
            scheduleHasParticipantsLinked={scheduleHasParticipantsLinked}
          />
        </div>
      )}

      {phase === 'FORMS' && (
        <div className="visit-pack-form-assignments">
          <FormAssignments
            visits={visits}
            setVisits={setVisits}
            visit={visit}
            setVisit={setVisit}
            forms={forms}
            setSpinnerText={setSpinnerText}
            context={context}
            showToaster={showToaster}
            showVisitForms={true}
            editMode={editMode}
            setVisitCodeEdited={() => undefined}
            study={study}
            enrolmentVisitAllowed={enrolmentVisitAllowed}
            scheduleHasParticipantsLinked={scheduleHasParticipantsLinked}
          />

          {isCancelModalOpen && (
            <CancelConfirmationModal
              closeCancelModal={closeCancelModal}
              handleCancelClick={handleCancelClick}
              setIsCancelModalOpen={setIsCancelModalOpen}
            />
          )}

          <div className={'visit-pack-sticky-footer'}>
            {editMode ? (
              <div className={'button-edit-container'}>
                <SaveButton
                  color={'#deab26'}
                  title={'Save Changes'}
                  onClick={handleUpdateSaveChangesClick}
                  disabled={fieldsDisabled}
                />
              </div>
            ) : (
              <div className={'button-edit-container'}>
                <SaveButton
                  color={'#ffffff'}
                  border={'1px solid #deab26'}
                  textColor={'#deab26'}
                  title={'Cancel'}
                  onClick={() => setIsCancelModalOpen(true)}
                  disabled={fieldsDisabled}
                />
                <SaveButton
                  color={'#deab26'}
                  title={'Save and Next'}
                  onClick={handleSaveAndNextClick}
                  disabled={fieldsDisabled}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
